/**
 * All page copy, per locale.
 *
 * Body copy, headings and UI strings come verbatim from the Claude Design source.
 * The additions are: page <title>/description (the canvas had no document head),
 * and the form status strings a live form needs but a mock-up did not
 * (`sending`, the validation messages, `failed`).
 *
 * Enquiry `subject` values stay in English on purpose — they travel in the email
 * to the office, which is read in one language regardless of the visitor's.
 */

export const LANGS = ['en', 'zh-Hant', 'zh-Hans', 'ja'];
export const DEFAULT_LANG = 'en';

/** URL path segment ⇄ language tag. `en` lives at the root. */
export const LANG_PATH = { en: '/', 'zh-Hant': '/zh-hant', 'zh-Hans': '/zh-hans', ja: '/ja' };
export const PATH_LANG = { 'zh-hant': 'zh-Hant', 'zh-hans': 'zh-Hans', ja: 'ja' };

/** Label for each language, always shown in that language. */
export const LANG_LABEL = { en: 'English', 'zh-Hant': '繁體中文', 'zh-Hans': '簡體中文', ja: '日本語' };

const SUBJECT_VALUES = [
  'Recording Production',
  'Promotion and Marketing',
  'Press Relations',
  'Data Analysis',
  'Something else',
];

export const CONTENT = {
  en: {
    htmlLang: 'en',
    meta: {
      title: 'Veni Music Company — Classical music in Hong Kong, Macao and mainland China',
      description:
        'Recording production, promotion and marketing, press relations and data analysis for artists, ensembles, venues and labels across Hong Kong, Macao and mainland China.',
    },
    nav: { services: 'Services', about: 'About', contact: 'Contact' },
    hero: {
      lines: ['The classical music business', 'of Hong Kong, Macao', 'and mainland China.'],
      lead: 'Veni Music Company works with artists, ensembles, venues and labels across the three territories: recording production, promotion and marketing, press relations, and the data analysis that tells you whether any of it worked.',
      primary: 'What we do',
      ghost: 'Get in touch',
    },
    territories: { places: ['Hong Kong', 'Macao', 'Mainland China'], years: 'Twenty years in practice' },
    services: {
      kicker: 'What we do',
      title: 'Four services, one catalogue mind.',
      items: [
        {
          title: 'Recording Production',
          body: 'Sessions planned, engineered and produced end to end: venue and instrument selection, session scheduling, editing and mastering, and delivery masters prepared to the specification your label or distributor actually asks for. Repertoire decisions come first; microphones come after.',
        },
        {
          title: 'Promotion and Marketing',
          body: 'Release campaigns built for the region rather than imported from elsewhere: streaming pitching, digital advertising, artist channels, and the local platforms that reach Chinese-language audiences. Every budget is set against what a release can plausibly return.',
        },
        {
          title: 'Press Relations',
          body: 'Working relationships with critics, editors, broadcasters and programme annotators in Hong Kong, Macao and the mainland. Press kits, interview preparation, review placement and bilingual materials for a press corps that reads in two languages and writes for readers who know the repertoire.',
        },
        {
          title: 'Data Analysis',
          body: 'Streaming, sales and audience figures read properly, and the tools to keep reading them: dashboards, catalogue reporting and campaign measurement built in house. Twenty years of software practice sits behind this, which is why the numbers arrive as answers rather than exports.',
        },
      ],
    },
    about: {
      kicker: 'About',
      title: 'Two disciplines, one desk',
      body: 'Hello, my name is Dennis Wu. I am a specialist of classical music and computer. Although I am exercising my brain quite separately on these wildly different areas, they become my profession of 20+ years.',
      portraitAlt: 'Dennis Wu, founder of Veni Music Company',
    },
    contact: { kicker: 'Contact', phone: 'Telephone', hk: 'Hong Kong', macau: 'Macau', web: 'Web' },
    write: {
      title: 'Write to us',
      body: 'Tell us about the release, the season or the campaign. Recordings, links and press material are welcome.',
      thanks: 'Thank you — your note is on its way.',
      thanksSub: 'We reply within two working days, from Hong Kong hours.',
      name: 'Name',
      namePh: 'Your name',
      email: 'Email',
      enquiry: 'Enquiry',
      message: 'Message',
      messagePh: 'A few lines about the project',
      send: 'Send enquiry',
      sending: 'Sending…',
      subjects: ['Recording Production', 'Promotion and Marketing', 'Press Relations', 'Data Analysis', 'Something else'],
      errRequired: 'This field is required.',
      errEmail: 'Please enter a valid email address.',
      errLong: 'This is longer than we can accept.',
      errFailed: 'The message could not be sent. Please try again, or email us directly.',
    },
    footer: { company: 'Veni Music Company', places: 'Hong Kong · Macao · Mainland China' },
  },

  'zh-Hant': {
    htmlLang: 'zh-Hant',
    meta: {
      title: 'Veni Music Company — 古典音樂事業，立足香港、澳門與中國內地',
      description:
        '與演奏家、樂團、演出場地及唱片公司在香港、澳門與中國內地合作：錄音製作、宣傳與市場推廣、媒體公關，以及數據分析。',
    },
    nav: { services: '服務', about: '關於', contact: '聯絡' },
    hero: {
      lines: ['古典音樂事業，', '立足香港、澳門', '與中國內地。'],
      lead: 'Veni Music Company 與演奏家、樂團、演出場地及唱片公司在三地合作：錄音製作、宣傳與市場推廣、媒體公關，以及工作是否奏效的數據分析。',
      primary: '服務內容',
      ghost: '聯絡我們',
    },
    territories: { places: ['香港', '澳門', '中國內地'], years: '二十年專業實踐' },
    services: {
      kicker: '服務',
      title: '四項服務，一套目錄思維。',
      items: [
        {
          title: '錄音製作',
          body: '由籌劃到交付，全程製作：場地與樂器選擇、錄音排期、剪輯與母帶處理，並按唱片公司或發行商實際要求準備交付母帶。曲目決定在先，麥克風在後。',
        },
        {
          title: '宣傳與市場推廣',
          body: '宣傳方案為本地市場而設，而非從外地照搬：串流平台推介、數位廣告、藝人自有頻道，以及能觸及華語聽眾的本地平台。每筆預算都對照一張唱片可合理帶來的回報而定。',
        },
        {
          title: '媒體公關',
          body: '與香港、澳門及內地的樂評人、編輯、廣播機構和樂曲解說撰稿人保持長期往來。新聞資料、訪問準備、評論安排，以及為雙語媒體與熟悉曲目的讀者而備的中英文材料。',
        },
        {
          title: '數據分析',
          body: '把串流、銷售與聽眾數據讀通，並持續備有讀取工具：自行開發的儀表板、目錄報表與宣傳成效量度。背後是二十年的軟件實踐，所以數字送到你手上時是答案，而不是匯出檔案。',
        },
      ],
    },
    about: {
      kicker: '關於',
      title: '兩門學問，同一張書桌',
      body: '你好，我叫 Dennis Wu。我是古典音樂與電腦兩方面的專家。雖然我在這兩個截然不同的領域上分別動腦，它們已成為我二十多年的專業。',
      portraitAlt: 'Veni Music Company 創辦人 Dennis Wu',
    },
    contact: { kicker: '聯絡', phone: '電話', hk: '香港', macau: '澳門', web: '網址' },
    write: {
      title: '來信',
      body: '告訴我們你的唱片、樂季或宣傳計劃。歡迎附上錄音、連結與媒體資料。',
      thanks: '謝謝，你的來信已寄出。',
      thanksSub: '我們會在兩個工作天內（香港時間）回覆。',
      name: '姓名',
      namePh: '你的姓名',
      email: '電郵',
      enquiry: '查詢類別',
      message: '內容',
      messagePh: '簡述你的計劃',
      send: '發送查詢',
      sending: '傳送中…',
      subjects: ['錄音製作', '宣傳與市場推廣', '媒體公關', '數據分析', '其他'],
      errRequired: '此欄必須填寫。',
      errEmail: '請輸入有效的電郵地址。',
      errLong: '內容超出可接受的長度。',
      errFailed: '訊息未能發送，請再試一次，或直接電郵給我們。',
    },
    footer: { company: 'Veni Music Company', places: '香港 · 澳門 · 中國內地' },
  },

  'zh-Hans': {
    htmlLang: 'zh-Hans',
    meta: {
      title: 'Veni Music Company — 古典音乐事业，立足香港、澳门与中国内地',
      description:
        '与演奏家、乐团、场馆及唱片公司在香港、澳门与中国内地合作：录音制作、宣传与市场推广、媒体公关，以及数据分析。',
    },
    nav: { services: '服务', about: '关于', contact: '联络' },
    hero: {
      lines: ['古典音乐事业，', '立足香港、澳门', '与中国内地。'],
      lead: 'Veni Music Company 与演奏家、乐团、场馆及唱片公司在三地合作：录音制作、宣传与市场推广、媒体公关，以及告诉你这一切是否奏效的数据分析。',
      primary: '服务内容',
      ghost: '联络我们',
    },
    territories: { places: ['香港', '澳门', '中国内地'], years: '二十年专业实践' },
    services: {
      kicker: '服务',
      title: '四项服务，一套目录思维。',
      items: [
        {
          title: '录音制作',
          body: '由筹划到交付，全程制作：场地与乐器选择、录音排期、剪辑与母带处理，并按唱片公司或发行商实际要求准备交付母带。曲目决定在先，麦克风在后。',
        },
        {
          title: '宣传与市场推广',
          body: '宣传方案为本地市场而设，而非从外地照搬：流媒体推介、数字广告、艺人自有频道，以及能触及华语听众的本地平台。每笔预算都对照一张唱片可合理带来的回报而定。',
        },
        {
          title: '媒体公关',
          body: '与香港、澳门及内地的乐评人、编辑、广播机构和乐曲解说撰稿人保持长期往来。新闻资料、访问准备、评论安排，以及为双语媒体与熟悉曲目的读者而备的中英文材料。',
        },
        {
          title: '数据分析',
          body: '把流媒体、销售与听众数据读通，并持续备有读取工具：自行开发的仪表板、目录报表与宣传成效量度。背后是二十年的软件实践，所以数字送到你手上时是答案，而不是导出文件。',
        },
      ],
    },
    about: {
      kicker: '关于',
      title: '两门学问，同一张书桌',
      body: '你好，我叫 Dennis Wu。我是古典音乐与电脑两方面的专家。虽然我在这两个截然不同的领域上分别动脑，它们已成为我二十多年的专业。',
      portraitAlt: 'Veni Music Company 创办人 Dennis Wu',
    },
    contact: { kicker: '联络', phone: '电话', hk: '香港', macau: '澳门', web: '网址' },
    write: {
      title: '来信',
      body: '告诉我们你的唱片、乐季或宣传计划。欢迎附上录音、链接与媒体资料。',
      thanks: '谢谢，你的来信已寄出。',
      thanksSub: '我们会在两个工作日内（香港时间）回复。',
      name: '姓名',
      namePh: '你的姓名',
      email: '电邮',
      enquiry: '咨询类别',
      message: '内容',
      messagePh: '简述你的计划',
      send: '发送咨询',
      sending: '发送中…',
      subjects: ['录音制作', '宣传与市场推广', '媒体公关', '数据分析', '其他'],
      errRequired: '此栏必须填写。',
      errEmail: '请输入有效的电邮地址。',
      errLong: '内容超出可接受的长度。',
      errFailed: '信息未能发送，请再试一次，或直接电邮给我们。',
    },
    footer: { company: 'Veni Music Company', places: '香港 · 澳门 · 中国内地' },
  },

  ja: {
    htmlLang: 'ja',
    meta: {
      title: 'Veni Music Company — クラシック音楽の仕事を、香港・マカオ・中国本土で',
      description:
        '演奏家・アンサンブル・会場・レーベルと香港、マカオ、中国本土で協働します。録音制作、プロモーションとマーケティング、プレス対応、データ分析。',
    },
    nav: { services: 'サービス', about: '会社について', contact: 'お問い合わせ' },
    hero: {
      lines: ['クラシック音楽の仕事を、', '香港・マカオ・中国本土で。'],
      lead: 'Veni Music Company は、演奏家・アンサンブル・会場・レーベルと三つの地域で協働します。録音制作、プロモーションとマーケティング、プレス対応、そしてその成果を確かめるためのデータ分析まで。',
      primary: '事業内容',
      ghost: 'お問い合わせ',
    },
    territories: { places: ['香港', 'マカオ', '中国本土'], years: '二十年の実務' },
    services: {
      kicker: '事業内容',
      title: '四つのサービス、一つの考え。',
      items: [
        {
          title: '録音制作',
          body: '企画から納品まで一貫して制作します。会場と楽器の選定、セッションの日程、編集とマスタリング、そしてレーベルや配信元が実際に求める仕様での納品マスター。まず曲目を決め、マイクはその後です。',
        },
        {
          title: 'プロモーションとマーケティング',
          body: '他地域の施策を持ち込むのではなく、この地域に合わせてキャンペーンを組み立てます。ストリーミングへの提案、デジタル広告、アーティスト自身のチャンネル、そして中国語圏のリスナーに届く現地プラットフォーム。予算は、そのリリースが現実に見込める成果に照らして決めます。',
        },
        {
          title: 'プレスリレーション',
          body: '香港、マカオ、中国本土の批評家、編集者、放送局、プログラムノート執筆者との継続的な関係。プレスキット、インタビューの準備、レビュー掲載の調整、そして二言語で読む記者と曲目に通じた読者に向けた資料を用意します。',
        },
        {
          title: 'データ分析',
          body: 'ストリーミング、セールス、オーディエンスの数字を正しく読み、読み続けるための道具も自社で作ります。ダッシュボード、カタログレポート、キャンペーン測定。背景にあるのは二十年のソフトウェア実務です。だから数字は、エクスポートではなく答えとして届きます。',
        },
      ],
    },
    about: {
      kicker: '会社について',
      title: '二つの専門、一つの机',
      body: 'こんにちは、Dennis Wu と申します。クラシック音楽とコンピュータの仕事専門職です。まったく異なるこの二つの分野で別々に頭を使ってきましたが、どちらも二十年以上にわたる私の仕事になりました。',
      portraitAlt: 'Veni Music Company 代表 Dennis Wu',
    },
    contact: { kicker: 'お問い合わせ', phone: '電話', hk: '香港', macau: 'マカオ', web: 'ウェブ' },
    write: {
      title: 'お問い合わせフォーム',
      body: 'リリース、シーズン、キャンペーンについてお知らせください。録音、リンク、プレス資料も歓迎します。',
      thanks: 'ありがとうございます。送信されました。',
      thanksSub: '香港時間の営業日2日以内にご返信します。',
      name: 'お名前',
      namePh: 'お名前',
      email: 'メール',
      enquiry: 'ご相談内容',
      message: 'メッセージ',
      messagePh: 'ご計画について数行お書きください',
      send: '送信',
      sending: '送信中…',
      subjects: ['録音制作', 'プロモーションとマーケティング', 'プレスリレーション', 'データ分析', 'その他'],
      errRequired: 'この項目は必須です。',
      errEmail: '有効なメールアドレスをご入力ください。',
      errLong: '入力が長すぎます。',
      errFailed: '送信できませんでした。もう一度お試しいただくか、直接メールをお送りください。',
    },
    footer: { company: 'Veni Music Company', places: '香港・マカオ・中国本土' },
  },
};

/** Office details — identical in every locale, so they live outside CONTENT. */
export const OFFICE = {
  legalName: 'Veni Music Company | Venti Music Ltd.',
  phones: ['HONG KONG +852 6212 2771', 'MACAU +853 6305 1845', 'MAINLAND CHINA +86 195 7546 2721'],
  hkAddress: 'Room 510, Wayson Commercial Building, 28 Connaught Road West, Hong Kong',
  macauAddress: 'Avenida Comercial De Macau, No. 70, FIT Centre, 5 Andar A, Posto 12046, Macau',
  web: { label: 'veni.hk', href: 'https://veni.hk' },
};

/** Pairs each localised option label with the English value sent to the office. */
export function subjectOptions(lang) {
  return CONTENT[lang].write.subjects.map((label, i) => ({ value: SUBJECT_VALUES[i], label }));
}
